import { useEffect, useState } from 'react';
import type { Artifact } from '@verifyloop/core';
import { artifactObjectUrl } from '../api/client';
import { artifactPath } from '../api/endpoints';
import { formatBytes, formatTimestamp } from '../lib/format';

export interface DomSanitizationResult {
  formattedText: string;
  redactionCount: number;
  hasRedactions: boolean;
}

/**
 * Best-effort secret masking for DOM snapshot text. This is not an HTML
 * sanitizer: callers must render the result as text, never as markup.
 */
export function sanitizeAndMaskDom(rawText: string): DomSanitizationResult {
  let redactions = 0;
  let formattedText = rawText.replace(
    /(<input\b[^>]*\btype\s*=\s*["']?password["']?[^>]*\bvalue\s*=\s*)(["'])(.*?)\2/gi,
    (_match, prefix, quote) => {
      redactions++;
      return `${prefix}${quote}[REDACTED:PASSWORD]${quote}`;
    },
  );
  formattedText = formattedText.replace(
    /(<input\b[^>]*\bvalue\s*=\s*)(["'])(.*?)\2([^>]*\btype\s*=\s*["']?password["']?[^>]*>)/gi,
    (_match, prefix, quote, _value, suffix) => {
      redactions++;
      return `${prefix}${quote}[REDACTED:PASSWORD]${quote}${suffix}`;
    },
  );
  formattedText = formattedText.replace(/(Bearer\s+)[A-Za-z0-9\-._~+/]+=*/gi, (_match, prefix) => {
    redactions++;
    return `${prefix}[REDACTED:TOKEN]`;
  });
  formattedText = formattedText.replace(
    /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\b/g,
    () => {
      redactions++;
      return '[REDACTED:JWT]';
    },
  );
  formattedText = formattedText.replace(
    /\b(?:jlk_|vlk_|vlt_|key_|sk_live_|sk_test_|ghp_|gho_|xoxb-|xoxp-)[A-Za-z0-9_-]{14,}\b/g,
    () => {
      redactions++;
      return '[REDACTED:API_KEY]';
    },
  );

  return {
    formattedText: formattedText.slice(0, 200_000),
    redactionCount: redactions,
    hasRedactions: redactions > 0,
  };
}

/**
 * Artifacts sit behind the bearer header, so a plain `<img src="/v1/...">` gets
 * a 401. They are fetched through the client and handed to the DOM as object
 * URLs instead, and every one is revoked when the component goes away.
 */
export function ArtifactPreview({
  runId,
  artifact,
  artifactUrl,
  compact = false,
  onPickSelector,
}: {
  runId: string;
  artifact: Artifact;
  /** Server-relative URL from `FailureBundleResponse.artifactUrls`, when present. */
  artifactUrl?: string;
  compact?: boolean;
  onPickSelector?: (selector: string) => void;
}): JSX.Element {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const path = artifactUrl ?? artifactPath(runId, artifact.id);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    setObjectUrl(null);
    setError(null);
    const controller = new AbortController();

    artifactObjectUrl(path, controller.signal)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setObjectUrl(url);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : String(cause));
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (created) URL.revokeObjectURL(created);
    };
  }, [path]);

  const label = `${artifact.kind}${artifact.stepIndex !== undefined ? ` · step ${artifact.stepIndex + 1}` : ''}`;
  const meta = `${artifact.kind} · ${formatBytes(artifact.byteSize)} · ${formatTimestamp(artifact.createdAt)}`;

  return (
    <figure className={`artifact${compact ? ' artifact--compact' : ''}`}>
      <figcaption className="artifact__caption">
        <span className="artifact__label">{label}</span>
        <span className="artifact__meta num" title={artifact.id}>
          {formatBytes(artifact.byteSize)} · {formatTimestamp(artifact.createdAt)}
        </span>
      </figcaption>

      {error ? (
        <p className="artifact__error" role="alert">
          {error}
        </p>
      ) : null}

      {!objectUrl && !error ? <div className="artifact__placeholder" aria-hidden="true" /> : null}

      {objectUrl ? (
        <ArtifactBody
          kind={artifact.kind}
          objectUrl={objectUrl}
          alt={`${artifact.kind} for ${label}`}
          onPickSelector={onPickSelector}
        />
      ) : null}

      {objectUrl ? (
        <div className="artifact__actions">
          {artifact.kind !== 'dom' ? (
            <>
              <a className="btn btn--ghost btn--small" href={objectUrl} target="_blank" rel="noreferrer">
                Open
              </a>
              <a className="btn btn--ghost btn--small" href={objectUrl} download={artifact.id}>
                Download
              </a>
            </>
          ) : null}
          <span className="visually-hidden">{meta}</span>
        </div>
      ) : null}
    </figure>
  );
}

export function DomSnapshotViewer({ url }: { url: string }): JSX.Element {
  const [data, setData] = useState<DomSanitizationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    fetch(url, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setData(sanitizeAndMaskDom(text));
      })
      .catch((err: unknown) => {
        if (!cancelled && !(err instanceof DOMException && err.name === 'AbortError')) {
          setError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [url]);

  if (error) {
    return (
      <p className="artifact__error" role="alert">
        Could not load DOM snapshot: {error}
      </p>
    );
  }

  if (!data) {
    return <div className="artifact__placeholder" aria-label="Loading DOM snapshot…" />;
  }

  return (
    <div className="artifact__dom-viewer">
      <p className="artifact__dom-notice">
        Inert text preview. DOM markup is not rendered, and remote resources are not loaded.
      </p>
      {data.hasRedactions ? (
        <p className="artifact__dom-notice" role="status">
          {data.redactionCount} recognizable secret pattern(s) masked. This best-effort masking may not identify every secret.
        </p>
      ) : null}
      <pre className="artifact__text artifact__text--code">
        <code>{data.formattedText}</code>
      </pre>
    </div>
  );
}

function ArtifactBody({
  kind,
  objectUrl,
  alt,
  onPickSelector,
}: {
  kind: Artifact['kind'];
  objectUrl: string;
  alt: string;
  onPickSelector?: (selector: string) => void;
}): JSX.Element {
  if (kind === 'screenshot') {
    return (
      <div style={{ position: 'relative', cursor: onPickSelector ? 'crosshair' : 'default' }}>
        <img
          className="artifact__image"
          src={objectUrl}
          alt={alt}
          loading="lazy"
          onClick={(e) => {
            if (!onPickSelector) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const xPercent = Math.round(((e.clientX - rect.left) / rect.width) * 100);
            const yPercent = Math.round(((e.clientY - rect.top) / rect.height) * 100);
            const selector = prompt(
              `Element clicked at coordinates (${xPercent}%, ${yPercent}%).\nEnter or refine selector (e.g. #button, button:has-text("Submit"), [data-testid="..."]):`,
              `[data-coord="${xPercent},${yPercent}"]`,
            );
            if (selector) {
              onPickSelector(selector.trim());
            }
          }}
        />
        {onPickSelector ? (
          <div
            style={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              background: 'var(--bg-panel)',
              color: 'var(--accent-text)',
              fontSize: 10.5,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 4,
              pointerEvents: 'none',
              backdropFilter: 'blur(4px)',
              border: '1px solid var(--border)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            🎯 Click screenshot to pick target selector
          </div>
        ) : null}
      </div>
    );
  }
  if (kind === 'video') {
    // eslint-disable-next-line jsx-a11y/media-has-caption -- captured UI video, no caption track
    return <video className="artifact__video" src={objectUrl} controls preload="metadata" />;
  }
  if (kind === 'dom') {
    return (
      <details className="artifact__dom" open>
        <summary>DOM snapshot (inert text preview)</summary>
        <DomSnapshotViewer url={objectUrl} />
      </details>
    );
  }
  return (
    <details className="artifact__dom">
      <summary>{kind}</summary>
      <ArtifactText url={objectUrl} />
    </details>
  );
}

function ArtifactText({ url }: { url: string }): JSX.Element {
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then((response) => response.text())
      .then((value) => {
        if (!cancelled) setText(value.slice(0, 200_000));
      })
      .catch(() => {
        if (!cancelled) setText('(could not read this artifact)');
      });
    return () => {
      cancelled = true;
    };
  }, [url]);
  return <pre className="artifact__text">{text ?? 'loading…'}</pre>;
}

export function ArtifactRow({
  runId,
  artifacts,
  title,
}: {
  runId: string;
  artifacts: readonly Artifact[];
  title?: string;
}): JSX.Element | null {
  if (artifacts.length === 0) return null;
  return (
    <section className="artifacts">
      {title ? <h4 className="artifacts__title">{title}</h4> : null}
      <div className="artifacts__grid">
        {/* A run-level artifact list can name the same artifact as a step, so the
            key is composite rather than the id alone. */}
        {artifacts.map((artifact, index) => (
          <ArtifactPreview key={`${artifact.id}:${index}`} runId={runId} artifact={artifact} compact />
        ))}
      </div>
    </section>
  );
}